import { FetchResult } from '@apollo/client';

import {
  CalculateBasicRateResponse,
  CalculateBasicRateVariables,
  ICurrency,
  IProposalMarineDetails
} from '../types';

import PrecisionService from 'src/core/services/PrecisionService';

export type SetMD = (name: keyof IProposalMarineDetails, v: any) => void;

/** Build onChange handler for currency select (pure) */
export const makeOnCurrencyChange = (
  currencySymbols: Record<string, string> | undefined,
  setMD: SetMD
) => {
  return (val: string) => {
    setMD('sumPerShipmentCurrency', val);
    const symbol = currencySymbols?.[val] ?? '';
    setMD('sumPerShipmentCurrencySymbol', symbol);
  };
};

/**
 * Compute conversion (pure). Throws on network/GraphQL error.
 * Caller (the widget) decides how to toast / set errors.
 */
export const computeShipmentConversion = async ({
  sumPerShipment,
  sumPerShipmentCurrency,
  policyCurrency,
  calcRate
}: {
  sumPerShipment: number;
  sumPerShipmentCurrency: string;
  policyCurrency: ICurrency;
  calcRate: (
    variables?: CalculateBasicRateVariables
  ) => Promise<FetchResult<CalculateBasicRateResponse>>;
}): Promise<{ rate: number; inPolicy: number }> => {
  const selected = sumPerShipmentCurrency || policyCurrency.id;

  if (selected === policyCurrency.id) {
    return {
      rate: 1,
      inPolicy: PrecisionService.round(Number(sumPerShipment))
    };
  }

  const [{ data: a }, { data: b }] = await Promise.all([
    calcRate({ currency: selected }),
    calcRate({ currency: policyCurrency.id })
  ]);

  const sumRate = a?.core?.CalculateBasicRate ?? 0;
  const polRate = b?.core?.CalculateBasicRate ?? 1;
  const rate = sumRate / polRate;
  const inPolicy = PrecisionService.round(Number(sumPerShipment) * rate, 3);

  return { rate, inPolicy };
};
